import { useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES } from '@/lib/constants/routes';
import { useResourceFolder } from './hooks/useResourceFolder';
import { ResourceList } from './components/ResourceList';
import { ResourceListSkeleton } from './components/ResourceListSkeleton';
import { ResourceDetailDialog } from './components/ResourceDetailDialog';
import { FolderHeading } from './components/FolderHeading';
import { resourceCoverUrl } from './resource-cover';
import { useOpenResource } from './hooks/useOpenResource';
import { RESOURCES_STRINGS } from './constants/resources-strings';

/**
 * Folder detail page — `/resources/folders/:slug`.
 *
 * Shows the folder's cover banner, centred title, description and count chip
 * (`FolderHeading`), then a list of the resources it contains. Renders an empty state when the folder has no items.
 */
export function FolderDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { data: folder, isLoading, isError, refetch } = useResourceFolder(slug);
  const folderCoverUrl = resourceCoverUrl(folder?.coverImageUrl);
  // Same resolver as the card's label: external links open in a new tab,
  // file-backed items open the detail dialog in place.
  const { dialogItem, openItem, closeDialog } = useOpenResource();

  return (
    <div className="bg-bg flex min-h-full flex-col">
      <PageHeader
        dense
        title={
          isLoading ? RESOURCES_STRINGS.PAGE_TITLE : (folder?.name ?? RESOURCES_STRINGS.PAGE_TITLE)
        }
        // Breadcrumb only: a bold folder title repeated the trail (UX sheet,
        // StartupAdminPatty r41).
        hideTitle
        breadcrumb={[
          { label: RESOURCES_STRINGS.PAGE_TITLE, to: ROUTES.RESOURCES },
          { label: folder?.name ?? '' },
        ]}
      />

      <div className="px-page-x flex flex-col gap-8 py-8">
        {/* Cover image banner; a folder with no cover keeps the neutral surface (MYN-343). */}
        {!isLoading && !isError && folder && (
          <div className="h-[280px] overflow-hidden rounded-xl bg-[#F7F9FB]">
            {folderCoverUrl && (
              <img src={folderCoverUrl} alt="" className="h-full w-full object-cover" />
            )}
          </div>
        )}
        {isLoading && <Skeleton className="h-[280px] w-full rounded-xl" />}

        {/* Centred title, description (when present) and count chip */}
        {!isLoading && !isError && folder && (
          <FolderHeading
            name={folder.name}
            description={folder.description}
            itemCount={folder.itemCount}
          />
        )}
        {isLoading && (
          // Same centred shape as FolderHeading (title line + count chip), so the
          // page does not shift when the folder loads.
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="h-10 w-1/2" />
            <Skeleton className="h-7 w-28 rounded-full" />
          </div>
        )}

        {/* Error state */}
        {isError && <ErrorState onRetry={() => void refetch()} />}

        {/* Resource list */}
        {isLoading && <ResourceListSkeleton />}
        {!isLoading && !isError && folder && folder.items.length === 0 && (
          <EmptyState
            title={RESOURCES_STRINGS.FOLDER_EMPTY.TITLE}
            description={RESOURCES_STRINGS.FOLDER_EMPTY.DESCRIPTION}
          />
        )}
        {!isLoading && !isError && folder && folder.items.length > 0 && (
          <section aria-label={`Resources in ${folder.name}`}>
            <ResourceList items={folder.items} onOpenItem={openItem} />
          </section>
        )}
      </div>

      <ResourceDetailDialog item={dialogItem} onClose={closeDialog} />
    </div>
  );
}
